// Calls: recent calls (incoming, outgoing, missed) with call-back buttons.
import { useState } from "react";
import { Link, useNavigate } from "react-router";
import { useQuery } from "@tanstack/react-query";
import { api } from "../../lib/api";
import { formatDuration, startCall, type CallHistoryItem, type CallMedia, type CallNavState } from "../../lib/calls";
import { formatDay, formatTime } from "../../lib/chat";
import { Alert, Avatar, Card, Spinner, cx } from "../../components/ui";
import { Icon } from "../../components/icons";
import { errorText, useOrg } from "../org";

export { IncomingCalls } from "./incoming";

function describe(c: CallHistoryItem): { text: string; tone: "red" | "slate" | "green" } {
  if (c.status !== "ended") return { text: "In progress", tone: "green" };
  if (c.direction === "incoming" && (c.myStatus === "missed" || c.endReason === "cancelled" && c.myStatus !== "joined" && c.myStatus !== "left")) return { text: "Missed", tone: "red" };
  if (c.direction === "incoming" && c.myStatus === "declined") return { text: "Declined", tone: "slate" };
  if (c.direction === "outgoing" && (c.endReason === "missed" || c.endReason === "declined")) return { text: c.endReason === "declined" ? "Declined" : "No answer", tone: "slate" };
  if (c.durationSeconds !== null) return { text: formatDuration(c.durationSeconds), tone: "slate" };
  return { text: c.endReason === "cancelled" ? "Cancelled" : "Ended", tone: "slate" };
}

export function CallsPage() {
  const { org } = useOrg();
  const navigate = useNavigate();
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const calls = useQuery({ queryKey: ["calls", org.slug], queryFn: () => api<{ calls: CallHistoryItem[] }>(`/orgs/${org.slug}/calls`) });

  async function callBack(c: CallHistoryItem, media: CallMedia) {
    setBusy(c.id + media); setError(null);
    try {
      const r = await startCall(org.slug, c.conversationId, media);
      navigate(`/o/${org.slug}/call/${r.call.id}`, { state: { join: r.join, call: r.call, media } satisfies CallNavState });
    } catch (err) { setError(errorText(err)); setBusy(null); }
  }

  let lastDay = "";
  return (
    <div className="mx-auto max-w-3xl">
      <div className="mb-6 flex flex-wrap items-end justify-between gap-3">
        <div><h1 className="text-2xl font-semibold tracking-tight">Calls</h1><p className="mt-1 text-sm text-slate-600 dark:text-slate-400">Start a call from any chat with the Video or Audio button.</p></div>
        <Link to={`/o/${org.slug}/recordings`} className="flex items-center gap-1.5 rounded-lg px-3 py-2 text-sm font-medium text-brand-700 hover:bg-brand-50 dark:text-brand-100 dark:hover:bg-brand-700/30"><Icon name="record" className="h-4 w-4" />Recordings</Link>
      </div>
      {error && <div className="mb-4"><Alert>{error}</Alert></div>}
      <Card className="p-0">
        {calls.isLoading && <div className="flex justify-center p-8 text-brand-600"><Spinner /></div>}
        {calls.data?.calls.length === 0 && (
          <div className="p-10 text-center">
            <div className="mx-auto mb-3 grid h-12 w-12 place-items-center rounded-2xl bg-brand-50 text-brand-600 dark:bg-brand-700/30"><Icon name="phone" /></div>
            <p className="font-medium">No calls yet</p>
            <p className="mt-1 text-sm text-slate-500">Open a chat in <Link to={`/o/${org.slug}/chat`} className="text-brand-600 hover:underline">Chat</Link> and press Video or Audio.</p>
          </div>
        )}
        <ul>
          {calls.data?.calls.map((c) => {
            const day = formatDay(c.startedAt);
            const header = day !== lastDay ? day : null;
            lastDay = day;
            const d = describe(c);
            return (
              <li key={c.id}>
                {header && <div className="border-b border-slate-100 bg-slate-50 px-5 py-1.5 text-xs font-semibold uppercase tracking-wide text-slate-500 dark:border-slate-800 dark:bg-slate-800/50">{header}</div>}
                <div className="flex items-center gap-3 border-b border-slate-100 px-5 py-3 last:border-0 dark:border-slate-800">
                  <Avatar name={c.title} />
                  <div className="min-w-0 flex-1">
                    <p className={cx("truncate font-medium", d.tone === "red" && "text-red-600")}>{c.title}</p>
                    <p className="flex items-center gap-1.5 text-sm text-slate-500">
                      <Icon name={c.media === "video" ? "video" : "phone"} className="h-3.5 w-3.5" />
                      {c.direction === "outgoing" ? "Outgoing" : "Incoming"} · <span className={cx(d.tone === "red" && "text-red-600", d.tone === "green" && "text-emerald-600")}>{d.text}</span> · {formatTime(c.startedAt)}
                    </p>
                  </div>
                  {c.available && (
                    <div className="flex gap-1">
                      <button type="button" onClick={() => callBack(c, "audio")} disabled={busy !== null} aria-label={`Audio call ${c.title}`} title="Audio call" className="rounded-lg p-2 text-brand-700 hover:bg-brand-50 disabled:opacity-50 dark:text-brand-100 dark:hover:bg-brand-700/30">{busy === c.id + "audio" ? <Spinner className="h-5 w-5" /> : <Icon name="phone" />}</button>
                      <button type="button" onClick={() => callBack(c, "video")} disabled={busy !== null} aria-label={`Video call ${c.title}`} title="Video call" className="rounded-lg p-2 text-brand-700 hover:bg-brand-50 disabled:opacity-50 dark:text-brand-100 dark:hover:bg-brand-700/30">{busy === c.id + "video" ? <Spinner className="h-5 w-5" /> : <Icon name="video" />}</button>
                      <Link to={`/o/${org.slug}/chat/${c.conversationId}`} aria-label={`Open chat with ${c.title}`} title="Open chat" className="rounded-lg p-2 text-slate-500 hover:bg-slate-100 dark:hover:bg-slate-800"><Icon name="chat" /></Link>
                    </div>
                  )}
                </div>
              </li>
            );
          })}
        </ul>
      </Card>
    </div>
  );
}
